import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { getFirebaseAuth } from "../Firebase/getFirebaseAuth";
import { TextInput } from "../UI/TextInput";

import { Card } from "../UI/Card";
import { PrimarySolidButton } from "../UI/PrimarySolidButton";
import { useTranslation } from "react-i18next";
import { Layout } from "../UI/Layout";
import { useParams, useSearchParams } from "react-router-dom";
import { decode } from "base-64";
import { Header } from "../UI/Header";
import { Footer } from "../UI/Footer";

type LProps = {
  onCodeSent: (confirmationResult: ConfirmationResult) => void;
};
export function LoginWithPhone(props: LProps) {
  const { t, i18n } = useTranslation();
  const auth = getFirebaseAuth();
  const [verifier, setVerifier] = useState<RecaptchaVerifier | null>(null);
  const recaptchaContainer = useRef<HTMLDivElement>(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(
    i18n.language === "it" ? "+39" : ""
  );
  const [showPhoneError, setShowPhoneError] = useState(false);

  useEffect(() => {
    if (!recaptchaContainer.current) throw new Error("No recaptcha container");
    setVerifier(
      new RecaptchaVerifier(
        recaptchaContainer.current,
        {
          size: "invisible",
        },
        auth
      )
    );
  }, [auth]);

  useEffect(() => {
    if (phoneNumber.length > 0 && !phoneNumber.startsWith("+"))
      setShowPhoneError(true);
    else setShowPhoneError(false);
  }, [phoneNumber]);

  async function sendCode() {
    if (phoneNumber.length < 7) return setShowPhoneError(true);
    if (!phoneNumber.startsWith("+")) return setShowPhoneError(true);
    if (!verifier) throw new Error("No verifier");
    try {
      setSendingCode(true);
      const res = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      props.onCodeSent(res);
    } finally {
      setSendingCode(false);
    }
  }
  return (
    <>
      <div ref={recaptchaContainer} />
      <Card className={"w-full"}>
        <div className={"flex items-center justify-center flex-col"}>
          <h1 className={"text-center  font-bold text-xl"}>
            {t("login.loginTitle")}
          </h1>
          <p className={"text-gray-600 text-center mb-4 mt-2"}>
            {t("login.loginSubTitle")}
          </p>

          <TextInput
            type={"tel"}
            autoFocus
            errorString={
              showPhoneError ? t("login.insertAValidNumber") : undefined
            }
            onEnterPressed={sendCode}
            value={phoneNumber}
            onChange={(v) => setPhoneNumber(v)}
            center={true}
            placeholder={t("login.yourNumber")}
          />
          <div className={"mt-4"}>
            <PrimarySolidButton
              onClick={sendCode}
              loading={sendingCode}
              label={t("login.sendCode")}
            />
          </div>
        </div>
      </Card>
    </>
  );
}

type IProps = {
  confirmResult: ConfirmationResult;
};
function InsertCode({ confirmResult }: IProps) {
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [showError, setShowError] = useState(false);
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const params = useParams();
  const redirect = search.get("r");
  const lang = params.lang;
  const confirmCode = useCallback(async () => {
    if (code.length < 6) return;
    try {
      setConfirming(true);
      await confirmResult.confirm(code);
      window.location.href = redirect ? decode(redirect) : `/${lang}`;
    } catch (e) {
      setShowError(true);
    } finally {
      setConfirming(false);
    }
  }, [code, confirmResult, lang, redirect]);

  useEffect(() => {
    (async function () {
      if (code.length === 6) await confirmCode();
    })();
  }, [code, confirmCode]);

  return (
    <Card className={"w-full"}>
      <div className={"flex items-center justify-center flex-col"}>
        <h1 className={"text-center mb-4 font-bold text-xl"}>
          {t("login.verificationCodeTitle")}
        </h1>
        <TextInput
          type={"number"}
          autoFocus
          errorString={showError ? t("login.invalidCode") : undefined}
          onEnterPressed={confirmCode}
          center={true}
          placeholder={t("login.yourCode")}
          value={code}
          onChange={(v) => {
            setShowError(false);
            setCode(v);
          }}
        />
        <div className={"mt-4"}>
          <PrimarySolidButton
            onClick={confirmCode}
            loading={confirming}
            label={t("login.confirm")}
          />
        </div>
      </div>
    </Card>
  );
}

export function LoginScreen() {
  const [confirmResult, setConfirmResult] = useState<ConfirmationResult>();
  return (
    <Layout>
      <Header />
      <div className={"flex flex-1 items-center w-full p-4"}>
        {confirmResult ? (
          <InsertCode confirmResult={confirmResult} />
        ) : (
          <LoginWithPhone
            onCodeSent={(confirm) => {
              setConfirmResult(confirm);
            }}
          />
        )}
      </div>
      <Footer />
    </Layout>
  );
}
